import { useEffect } from 'react'

import { isOnboardingPath } from '@/features/onboarding/lib/step-routes'
import { decodeCurrentAuthJwtClaims } from '@/lib/auth/better-auth-http'
import { getWorkerBearerToken } from '@/lib/auth/client'
import { primeCustomerBootstrap } from '@/lib/customer-bootstrap'

/**
 * Parallel-bootstrap prefetch registry.
 *
 * The CustomerLayout gate blocks every customer page render on the customer
 * bootstrap fetch (`/api/customer/bootstrap`). A page's own data query
 * (`useCustomerQuery`) therefore cannot start until AFTER bootstrap resolves —
 * two serial round trips. Measured cost (Sentry, 30d): bootstrap ~0.9s p50 /
 * ~2.1s p95, federal-tax landing ~0.86s p50 / ~3.4s p95; serial = the sum.
 *
 * A page whose query does not depend on bootstrap data can instead be primed IN
 * PARALLEL with bootstrap the moment auth is loaded — so its data is already
 * warm in the shared `useCustomerQuery` cache by the time the gate opens and the
 * page mounts. This turns the serial sum into the parallel max.
 *
 * Each entry lazily imports a leaf query module so the feature's page/UI code
 * stays out of the CustomerLayout critical-path chunk and only loads on its own
 * route. Add a slow customer page here to opt it into the same treatment. A
 * read scoped by the company starts at once all the same and is filed under the
 * company when the bootstrap names it (`lib/customer-prefetch`). The hook runs
 * on every navigation to an opted-in route, not only the first load; the reads
 * are shared with the page's own (in-flight dedupe), so that costs nothing.
 */
export type ParallelBootstrapPrefetch = (userId: string) => Promise<unknown>

const PARALLEL_BOOTSTRAP_PREFETCH_BY_ROUTE: Record<string, () => Promise<ParallelBootstrapPrefetch>> = {
  '/': () => import('@/features/dashboard/lib/home-prefetch').then((m) => m.prefetchHome),
  '/federal-tax': () => import('@/features/federal-tax/lib/landing-query').then((m) => m.prefetchFederalTaxLanding),
  '/state-filings': () =>
    import('@/features/state-filings/lib/landing-prefetch').then((m) => m.prefetchStateFilingsLanding),
  '/accounting': () => import('@/features/accounting/lib/overview-prefetch').then((m) => m.prefetchAccountingPage),
  '/accounting/transactions': () =>
    import('@/features/accounting/lib/transactions-first-page').then((m) => m.prefetchTransactionsFirstPage),
  // The mailroom page waits on the company's services (its entitlement) as well as its first page.
  '/mailroom': () =>
    Promise.all([
      import('@/features/mailroom/lib/mailroom-prefetch'),
      import('@/features/services/lib/company-services-prefetch'),
    ]).then(
      ([mailroom, services]) =>
        (userId: string) =>
          Promise.all([mailroom.prefetchMailroom(userId), services.prefetchCompanyServices(userId)]),
    ),
}

export function getParallelBootstrapPrefetch(pathname: string): (() => Promise<ParallelBootstrapPrefetch>) | undefined {
  return PARALLEL_BOOTSTRAP_PREFETCH_BY_ROUTE[pathname]
}

/** The billing banner sits above every customer page except the onboarding wizard. */
const loadBillingHealthPrefetch = () =>
  import('@/features/billing/hooks/use-billing-health').then((m) => m.prefetchBillingHealth)

/** Every prefetch a customer page at `pathname` wants: the shell's own reads, then the route's. */
export function getParallelBootstrapPrefetches(pathname: string): (() => Promise<ParallelBootstrapPrefetch>)[] {
  const loaders = isOnboardingPath(pathname) ? [] : [loadBillingHealthPrefetch]
  const routeLoader = getParallelBootstrapPrefetch(pathname)
  return routeLoader ? [...loaders, routeLoader] : loaders
}

/**
 * The hard-load half of the same idea, called once from the SPA entry
 * (`spa/main.tsx`) before React renders, for a location the customer shell
 * draws.
 *
 * On a reload nothing used to start before the guard's get-session answered:
 * then /token, then bootstrap, then billing-health — four round trips in a row.
 * Every one of those reads needs only the JWT, and `/token` itself answers 401
 * without a session, so this asks for the JWT at once (in parallel with
 * get-session) and starts bootstrap and the prefetches from its `sub`, the same
 * Better Auth user id the session later names. The guard and CustomerLayout
 * still decide what renders; they find their answers already in flight or
 * cached (`primeCustomerBootstrap`, the `useCustomerQuery` cache).
 *
 * Best-effort and never rejects: on any failure the layout and pages fetch for
 * themselves exactly as before.
 */
export async function startHardLoadPrefetch(pathname: string): Promise<void> {
  // The whole body, synchronous parts included: main.tsx calls this with
  // `void`, so anything that escaped would be an unhandled rejection.
  try {
    // The leaf query chunks download while /token is in flight.
    const prefetches = getParallelBootstrapPrefetches(pathname).map((load) => load())
    for (const prefetch of prefetches) prefetch.catch(() => undefined)

    const token = await getWorkerBearerToken()
    const userId = token ? decodeCurrentAuthJwtClaims(token)?.sub : null
    if (!userId) return

    primeCustomerBootstrap(userId)
    await Promise.all(prefetches.map((prefetch) => prefetch.then((run) => run(userId)).catch(() => undefined)))
  } catch {
    // Best-effort: the layout and pages fetch for themselves.
  }
}

/**
 * Fires the shell's and the opted-in route's prefetches once auth is loaded, in
 * parallel with the CustomerLayout bootstrap fetch (after a hard load they are
 * already warm from `startHardLoadPrefetch`, and this only re-reads the cache).
 * No-op before auth loads. Best-effort: any failure is swallowed and the page falls
 * back to its own `useCustomerQuery` fetch (today's behavior).
 */
export function useParallelBootstrapPrefetch(params: {
  isLoaded: boolean
  userId: string | null | undefined
  pathname: string
}): void {
  const { isLoaded, userId, pathname } = params
  useEffect(() => {
    if (!isLoaded || !userId) {
      return
    }
    const loaders = getParallelBootstrapPrefetches(pathname)
    if (loaders.length === 0) {
      return
    }

    let cancelled = false
    for (const loader of loaders) {
      void loader()
        .then((prefetch) => {
          if (cancelled) {
            return undefined
          }
          return prefetch(userId)
        })
        .catch(() => {
          // Best-effort warm-up. The page's own useCustomerQuery fetch remains the
          // source of truth if this prefetch fails.
        })
    }

    return () => {
      cancelled = true
    }
  }, [isLoaded, userId, pathname])
}
